import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { OrganizationSettingsService, type LogoPatch, type OrgProfileFields } from './OrganizationSettingsService';

/** ~512 KB decoded ceiling (matches auth-api); base64 data-URL runs ~1.37× larger. */
const LOGO_MAX_DATAURL_BYTES = 700 * 1024;

/**
 * Drives Settings → Organization: loads the current tenant's org profile from
 * auth-api, edits it locally, and persists via PATCH. The company name maps to
 * the tenant name; the text profile lives in the tenant `config.profile`; the
 * logo is uploaded/served out-of-band via blob storage.
 */
@injectable()
export class OrganizationSettingsViewModel extends BaseViewModel {
    loading = true;
    saving = false;
    loadError: string | null = null;
    saveError: string | null = null;
    memberCount = 0;

    name = '';
    description = '';
    website = '';
    industry: string | null = null;
    region: string | null = null;
    size: string | null = null;

    /** What to show in the logo slot: a live preview (pending upload) or the stored blob URL. */
    logoUrl: string | null = null;
    /** Pending logo change to send on save; `undefined` means "leave the stored logo as-is". */
    private pendingLogo: LogoPatch = undefined;

    private baseline = '';

    constructor(private readonly service: OrganizationSettingsService) {
        super();
    }

    async initialize(): Promise<void> {
        this.loading = true;
        this.loadError = null;
        this.notifyChange();
        try {
            this.apply(await this.service.load());
        } catch (cause) {
            this.loadError = cause instanceof Error ? cause.message : String(cause);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    get isDirty(): boolean {
        return this.snapshot() !== this.baseline;
    }

    setName(value: string): void { this.name = value; this.notifyChange(); }
    setDescription(value: string): void { this.description = value; this.notifyChange(); }
    setWebsite(value: string): void { this.website = value; this.notifyChange(); }
    setIndustry(value: string | null): void { this.industry = value; this.notifyChange(); }
    setRegion(value: string | null): void { this.region = value; this.notifyChange(); }
    setSize(value: string | null): void { this.size = value; this.notifyChange(); }

    async setLogoFile(file: File | null): Promise<void> {
        this.saveError = null;
        if (!file) {
            this.pendingLogo = null;
            this.logoUrl = null;
            this.notifyChange();
            return;
        }
        const dataUrl = await readAsDataUrl(file);
        if (dataUrl.length > LOGO_MAX_DATAURL_BYTES) {
            this.saveError = 'Logo is too large — use an image under ~500 KB.';
            this.notifyChange();
            return;
        }
        this.pendingLogo = dataUrl;
        this.logoUrl = dataUrl;
        this.notifyChange();
    }

    async save(): Promise<boolean> {
        this.saving = true;
        this.saveError = null;
        this.notifyChange();
        try {
            this.apply(await this.service.save({ name: this.name.trim(), profile: this.fields(), logo: this.pendingLogo }));
            return true;
        } catch (cause) {
            this.saveError = cause instanceof Error ? cause.message : String(cause);
            return false;
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    private fields(): OrgProfileFields {
        return {
            description: this.description,
            website: this.website,
            industry: this.industry,
            region: this.region,
            size: this.size,
        };
    }

    private apply(settings: { name: string; memberCount: number; profile: OrgProfileFields; logoUrl: string | null }): void {
        this.name = settings.name;
        this.memberCount = settings.memberCount;
        this.description = settings.profile.description;
        this.website = settings.profile.website;
        this.industry = settings.profile.industry;
        this.region = settings.profile.region;
        this.size = settings.profile.size;
        this.logoUrl = settings.logoUrl;
        this.pendingLogo = undefined;
        this.baseline = this.snapshot();
    }

    private snapshot(): string {
        const logoState = this.pendingLogo === undefined ? 'keep' : this.pendingLogo === null ? 'remove' : 'new';
        return JSON.stringify([this.name, this.description, this.website, this.industry, this.region, this.size, logoState]);
    }
}

function readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (): void => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = (): void => reject(reader.error ?? new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}
